import React, { useState, useCallback, useEffect } from "react";

import {
  fetchOrganizations,
  refreshPCCActivations,
  uploadContractDocument,
} from "../../services/organizations";
import { Column, Organization } from "../../ts/types";
import {
  getClasses,
  getExtensionFromUri,
  getFacilityFullAddress,
  getFormattedDateTime,
} from "../../utils";
import PanelContainer from "../../components/Containers/PanelContainer";
import PanelActionContainer from "../../components/Containers/PanelActionContainer";
import PanelInnerContainer from "../../components/Containers/PanelInnerContainer";
import Card from "../../components/Card/Card";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import Table from "../../components/CustTable/Table";
import Drawer from "../../components/Drawer/Drawer";
import Input from "../../components/Inputs/Input";
import Status from "../../components/Status/Status";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableContainer from "../../components/CustTable/TableContainer";
import LinkButton from "../../components/Buttons/LinkButton";
import DragDropField from "../../components/Inputs/DragDropField";
import classes from "./Organizations.module.css";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import ClosableBox from "../../components/Box/ClosableBox";
import DataField from "../../components/Box/DataField";

type DrawerMode = "view" | "edit";

interface DrawerState {
  org: Organization;
  mode: DrawerMode;
}

const BASE = "/api";

const getDocumentUrl = (
  url?: string,
  updateTime?: number,
  tenantId?: string,
) => {
  if (!url || !tenantId) {
    return "";
  }
  return `${BASE}${url}?${updateTime}&tenantId=${tenantId}`;
};

const Organizations: React.FC = () => {
  const resourceName = useSelector(
    (state: RootState) => state.auth.resourceName,
  );
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [drawerState, setDrawerState] = useState<DrawerState | null>(null);

  const handleRefreshActivations = async () => {
    try {
      const res = await refreshPCCActivations();
      if (res.data.success) {
        handleFetchOrgs();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleFetchOrgs = useCallback(async () => {
    setIsFetching(true);
    setFetchError(null);
    try {
      const records = await fetchOrganizations();
      setOrgs(records.data.orgs);
    } catch (err) {
      setFetchError(
        "Failed to fetch activations from PCC. Please check your credentials and try again.",
      );
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    handleFetchOrgs();
  }, []);

  const handleUpdate = async (id: string, notes: string, contractDoc?: any) => {
    const formData = new FormData();
    formData.append("notes", notes);
    if (contractDoc && typeof contractDoc !== "string") {
      formData.append("contractDocument", contractDoc);
    }

    try {
      const res = await uploadContractDocument(id, formData);
      if (res.status === 200) {
        setOrgs((prev) => prev.map((o) => (o.id === id ? res.data : o)));
      }
    } catch (err) {
      console.log(err);
    }
    setDrawerState(null);
  };

  const filtered = orgs.filter((o) =>
    o?.orgName?.toLowerCase().includes(searchText.toLowerCase()),
  );
  const activeCount = orgs.filter((o) => o.isActive).length;
  const inactiveCount = orgs.filter((o) => !o.isActive).length;

  const columns: Column<Organization>[] = [
    {
      header: "ORG NAME",
      width: "30%",
      accessor: (org) => (
        <span className={classes.pccField}>{org.orgName}</span>
      ),
      style: { textAlign: "left" },
    },
    {
      header: "FACILITIES",
      width: "15%",
      accessor: (org) => (
        <span className={classes.pccField}>
          {org.facilities.length > 0
            ? `${org.facilities.length} facilit${org.facilities.length > 1 ? "ies" : "y"}`
            : "—"}
        </span>
      ),
      style: { textAlign: "left" },
    },
    {
      header: "LAST SYNCED",
      width: "18%",
      accessor: (org) => (
        <span className={classes.pccField}>
          {org.lastSyncedTime ? getFormattedDateTime(org.lastSyncedTime) : "—"}
        </span>
      ),
      style: { textAlign: "left" },
    },
    {
      header: "CONTRACT",
      width: "15%",
      accessor: (org) =>
        org?.contractDocument ? (
          <a
            href={
              getDocumentUrl(org?.contractDocument, Date.now(), resourceName) ??
              "#"
            }
            rel='noreferrer'
            target='_blank'
            className={classes.contractLink}
            onClick={(e) => e.stopPropagation()}>
            {`org_document.${getExtensionFromUri(org?.contractDocument)}`}
          </a>
        ) : (
          <span className={classes.noContract}>None</span>
        ),
      style: { textAlign: "left" },
    },
    {
      header: "STATUS",
      width: "12%",
      accessor: (org) => (
        <Status
          name={org.isActive ? "Active" : "Inactive"}
          status={org.isActive ? "active" : "closed"}
        />
      ),
      style: { textAlign: "left" },
    },
    {
      header: "ACTION",
      width: "10%",
      accessor: (org) => (
        <LinkButton
          label='View'
          onClick={() => setDrawerState({ org, mode: "view" })}
        />
      ),
    },
  ];

  return (
    <>
      <PanelContainer name='Organizations'>
        <PanelActionContainer
          leftChildren={
            <div className={classes.statsRow}>
              <div className={classes.stat}>
                <span className={classes.statValue}>{orgs.length}</span>
                <span className={classes.statLabel}>Total</span>
              </div>
              <div className={classes.stat}>
                <span className={`${classes.statValue} ${classes.activeColor}`}>
                  {activeCount}
                </span>
                <span className={classes.statLabel}>Active</span>
              </div>
              <div className={classes.stat}>
                <span
                  className={`${classes.statValue} ${classes.inactiveColor}`}>
                  {inactiveCount}
                </span>
                <span className={classes.statLabel}>Inactive</span>
              </div>
            </div>
          }
          rightChildren={
            <div className={classes.actionsRight}>
              <PrimaryButton
                name={"Refresh from PointClickCare"}
                onClick={handleRefreshActivations}
                disabled={isFetching}
              />
            </div>
          }
        />

        {/* Error banner */}
        {fetchError ? (
          <Card className={classes.errorCard}>
            <p className={classes.errorText}>{fetchError}</p>
          </Card>
        ) : (
          <></>
        )}

        <Card className={classes.filterCard}>
          <SearchBar
            onChange={(searchText) => setSearchText(searchText)}
            placeholder='Search by Org Name'
          />
        </Card>

        <PanelInnerContainer className={classes.selectableText}>
          {/* <div className={classes.pccNotice}>
            <span className={classes.pccDot} /> PCC-sourced fields are
            read-only.&nbsp;&nbsp;
            <span className={classes.reyaDot} /> White fields are editable in
            ReyaVax.
          </div> */}
          <TableContainer>
            <Table columns={columns} data={filtered} />
          </TableContainer>
          <p className={classes.recordCount}>
            Showing {filtered.length} of {orgs.length} organizations
          </p>
        </PanelInnerContainer>
      </PanelContainer>

      {drawerState && (
        <OrgDrawer
          org={drawerState.org}
          mode={drawerState.mode}
          resourceName={resourceName}
          onModeChange={(mode) => setDrawerState({ ...drawerState, mode })}
          onSave={handleUpdate}
          onClose={() => setDrawerState(null)}
        />
      )}
    </>
  );
};

interface OrgDrawerProps {
  org: Organization;
  mode: DrawerMode;
  resourceName: string;
  onModeChange: (mode: DrawerMode) => void;
  onSave: (id: string, notes: string, contractDoc: any) => void;
  onClose: () => void;
}

const OrgDrawer: React.FC<OrgDrawerProps> = ({
  org,
  mode,
  resourceName,
  onModeChange,
  onSave,
  onClose,
}) => {
  const uploadedDoc = org?.contractDocument
    ? `org_document.${getExtensionFromUri(org?.contractDocument)}`
    : "";
  const [contractDoc, setContractDoc] = useState<any>(uploadedDoc);
  const [notes, setNotes] = useState<string>(org?.notes ?? "");

  const handleSave = () => {
    onSave(org.id, notes, contractDoc);
  };

  return (
    <Drawer name='Organization Details' onClose={onClose}>
      <div
        className={getClasses(classes.drawerContent, classes.selectableText)}>
        <Card className={classes.card}>
          <section className={classes.section}>
            <div className={classes.sectionHeader}>
              <span className={classes.pccDot} />
              <span>From PointClickCare (read-only)</span>
            </div>

            <div className={classes.fieldRow}>
              <span className={classes.fieldLabel}>Org Name</span>
              <span className={`${classes.fieldValue} ${classes.pccValue}`}>
                {org.orgName}
              </span>
            </div>

            <div className={classes.fieldRow}>
              <span className={classes.fieldLabel}>Org UUID</span>
              <span className={`${classes.fieldValue} ${classes.pccValue}`}>
                {org.id}
              </span>
            </div>

            <div className={classes.fieldRow}>
              <span className={classes.fieldLabel}>Status</span>
              <Status
                name={org.isActive ? "Active" : "Inactive"}
                status={org.isActive ? "active" : "closed"}
              />
            </div>

            <div className={classes.fieldRow}>
              <span className={classes.fieldLabel}>Last Synced</span>
              <span className={`${classes.fieldValue} ${classes.pccValue}`}>
                {org.lastSyncedTime
                  ? getFormattedDateTime(org.lastSyncedTime)
                  : "—"}
              </span>
            </div>
            <>
              {org.facilities.length > 0 && (
                <>
                  <p className={classes.facilitiesTitle}>
                    Facilities ({org.facilities.length})
                  </p>
                  <div className={classes.facilitiesSection}>
                    {org.facilities.map((fac) => (
                      <div key={fac.facId} className={classes.facilityRow}>
                        <span
                          className={`${classes.fieldValue} ${classes.pccValue}`}>
                          {fac.facilityName}
                        </span>
                        <DataField
                          name='Address'
                          data={getFacilityFullAddress(fac)}
                          className={classes.dataField}
                        />
                        <DataField
                          name='Email'
                          data={fac.emailAddress ?? "-"}
                          className={classes.dataField}
                        />
                        <DataField
                          name='Phone'
                          data={fac.phone ?? "-"}
                          className={classes.dataField}
                        />
                        <DataField
                          name='Fax'
                          data={fac.fax ?? "-"}
                          className={classes.dataField}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          </section>
        </Card>

        <Card className={classes.card}>
          <section className={classes.section}>
            <div className={classes.sectionHeader}>
              <span className={classes.reyaDot} />
              <span>ReyaVax Data (editable)</span>
            </div>

            {mode === "view" ? (
              <>
                <div className={classes.fieldRow}>
                  <span className={classes.fieldLabel}>Contract</span>
                  <span className={classes.fieldValue}>
                    {uploadedDoc ? (
                      <a
                        href={
                          getDocumentUrl(
                            org?.contractDocument,
                            Date.now(),
                            resourceName,
                          ) ?? "#"
                        }
                        rel='noreferrer'
                        target='_blank'
                        className={classes.contractLink}
                        onClick={(e) => e.stopPropagation()}>
                        {uploadedDoc}
                      </a>
                    ) : (
                      "_"
                    )}
                  </span>
                </div>
                <div className={classes.fieldRow}>
                  <span className={classes.fieldLabel}>Internal Notes</span>
                  <span
                    style={{ maxWidth: "13rem" }}
                    className={classes.fieldValue}>
                    {org?.notes || "—"}
                  </span>
                </div>
                <div className={classes.drawerActions}>
                  <PrimaryButton
                    name='Edit'
                    onClick={() => onModeChange("edit")}
                  />
                </div>
              </>
            ) : (
              <>
                <DragDropField
                  name=''
                  onChange={(file: any) => setContractDoc(file)}
                  selected={true}
                  fileTypes={["pdf", "docx"]}
                  mainClassName={classes.dragDropMain}
                  iconClassName={classes.uploadIcon}
                  className={classes.dragDropField}>
                  <p className={classes.uploadLabel}>Contract Document</p>
                  <p className={classes.uploadHint}>
                    {contractDoc
                      ? `📄 ${typeof contractDoc === "string" ? contractDoc : contractDoc.name}`
                      : ""}
                  </p>
                </DragDropField>
                <Input
                  name='Internal Notes'
                  value={notes ?? ""}
                  textarea
                  placeholder='Add any internal notes about this organization...'
                  onChange={(e) => setNotes(e.target.value)}
                />

                <div className={classes.drawerActions}>
                  <PrimaryButton name='Save' onClick={handleSave} />
                  <PrimaryButton
                    name='Cancel'
                    transparent
                    onClick={() => onModeChange("view")}
                  />
                </div>
              </>
            )}
          </section>
        </Card>
      </div>
    </Drawer>
  );
};

export default Organizations;
